import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import ModernScheduleInterview from "../../../components/shared_components/ModernScheduleInterview";
import host from "../../../global";
import { confirmCompanyDetails, useCompanyDetailsGate } from "../../../utils/companyProfileCheck";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

/**
 * Single-candidate "Schedule Interview" flow, reusable anywhere a candidate is shown.
 * Every search/job role is tied to a GenieHire job, so the job is known directly — no
 * project or link step. Flow: check email (prompt if missing) → open schedule popup →
 * create the interview via the same store_candidate_data endpoint the ATS uses →
 * hand the candidate over to Interview Management.
 *
 * Scheduling is a move, not a copy: once the interview exists the candidate is tracked in
 * Interview Management, so `/sourcing/scheduled` marks them and every sourcing list stops
 * returning them. `onScheduled(candidateIds)` fires after that mark lands, so the caller
 * can drop the rows without waiting for a refetch. It is deliberately not called when the
 * mark fails — the server would still return those rows and they would reappear.
 *
 * Usage:
 *   const { startSchedule, scheduleUI, canSchedule } = useScheduleInterview(jobId, onScheduled);
 *   ... {canSchedule && <button onClick={() => startSchedule(candidate)}>…</button>}
 *   {scheduleUI}
 */
export default function useScheduleInterview(jobId, onScheduled) {
    const orgId = useSelector(state => state.userSlice?.org_id) || localStorage.getItem("current_org_id");
    const accessToken = sessionStorage.getItem("accessToken");

    const [jobConfig, setJobConfig] = useState(null);
    const [scheduleTargets, setScheduleTargets] = useState(null); // array of candidates being scheduled (1 or many)
    // The company name question comes before the date and time, not after
    const schedulePopupOpen = useCompanyDetailsGate(scheduleTargets?.length > 0, () => setScheduleTargets(null));
    const [scheduling, setScheduling] = useState(false);
    const [emailPromptFor, setEmailPromptFor] = useState(null);
    const [emailInput, setEmailInput] = useState("");
    const [emailSaving, setEmailSaving] = useState(false);
    const [bulkGate, setBulkGate] = useState(null); // [{candidate, email, adding, value}] — missing-email gate

    // Interview configuration is only needed once the recruiter actually schedules, so it
    // is fetched on demand in openSchedule (which already loads it when missing or stale)
    // rather than on mount — preloading here cost one API call on every page load, for a
    // feature most visits never touch. Reset on job change so a previous job's config can
    // never be reused.
    useEffect(() => {
        setJobConfig(null);
    }, [jobId]);

    const loadJobConfig = async (id) => {
        const { data } = await axios.get(`${SOURCING_URL}/sourcing/jobs/${id}`);
        setJobConfig(data);
        return data;
    };

    // Emails are hidden until revealed, so the list payload's `email` is often null even
    // when one is on file (professional/personal). Resolve it from the DB (no CrustData).
    const resolveEmail = async (candidate) => {
        if (candidate.email) return candidate.email;
        try {
            const { data } = await axios.get(`${SOURCING_URL}/sourcing/candidate/${candidate.candidate_id}/contact`);
            return data?.email || null;
        } catch {
            return null;
        }
    };

    // Single: resolve the candidate's email (prompt to add one only if truly missing).
    const startSchedule = async (candidate) => {
        if (!jobId) { toast.warn("This search isn't tied to a job role yet."); return; }
        const email = await resolveEmail(candidate);
        if (!email) {
            setEmailInput("");
            setEmailPromptFor({ candidate });
            return;
        }
        openSchedule([{ ...candidate, email }]);
    };

    // Bulk: resolve everyone's email first. Those on file are ready; any still missing open
    // the email gate (add manually) instead of being silently dropped.
    const startBulkSchedule = async (candidates) => {
        if (!jobId) { toast.warn("This search isn't tied to a job role yet."); return; }
        const list = candidates || [];
        if (list.length === 0) return;
        const resolved = await Promise.all(list.map(async c => ({ ...c, email: await resolveEmail(c) })));
        const missing = resolved.filter(c => !c.email);
        if (missing.length === 0) {
            openSchedule(resolved);
            return;
        }
        // Some need an email — open the gate so the recruiter can add them or skip.
        setBulkGate(resolved.map(c => ({ candidate: c, email: c.email, adding: false, value: "" })));
    };

    const saveEmailAndContinue = async () => {
        const { candidate } = emailPromptFor;
        const email = emailInput.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            toast.warn("Please enter a valid email address.");
            return;
        }
        setEmailSaving(true);
        try {
            await axios.post(`${SOURCING_URL}/sourcing/add-email`, { candidate_id: candidate.candidate_id, email });
            setEmailPromptFor(null);
            openSchedule([{ ...candidate, email }]);
        } catch {
            toast.error("Couldn't save that email. Please try again.");
        } finally {
            setEmailSaving(false);
        }
    };

    // ── Bulk email gate helpers ──────────────────────────────────────────────
    const gateAddEmail = async (candidateId) => {
        const row = bulkGate.find(r => r.candidate.candidate_id === candidateId);
        const email = (row?.value || "").trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.warn("Please enter a valid email address."); return; }
        try {
            await axios.post(`${SOURCING_URL}/sourcing/add-email`, { candidate_id: candidateId, email });
            setBulkGate(prev => prev.map(r => r.candidate.candidate_id === candidateId
                ? { ...r, email, adding: false, value: "" } : r));
        } catch {
            toast.error("Couldn't save that email. Please try again.");
        }
    };

    const gateContinue = () => {
        const ready = bulkGate.filter(r => r.email).map(r => ({ ...r.candidate, email: r.email }));
        if (ready.length === 0) { toast.warn("Add at least one email to continue."); return; }
        setBulkGate(null);
        openSchedule(ready);
    };

    const openSchedule = async (candidates) => {
        let cfg = jobConfig;
        if (!cfg || cfg.job_id !== jobId) {
            try { cfg = await loadJobConfig(jobId); }
            catch { toast.error("Couldn't load the job's interview configuration."); return; }
        }
        setScheduleTargets(candidates);
    };

    const _postOne = async (c, cfg, { startDate, endDate, timezone, timeslot }) => {
        const fd = new FormData();
        fd.append("name", c.full_name || "Candidate");
        fd.append("candidate_email", c.email || "");
        fd.append("mobile", c.phone || c.professional_phone || c.personal_phone || c.mobile || "");
        fd.append("mcq_set_id", cfg.mcq_set_id || "");
        fd.append("Coding_Judge_Criteria", cfg.coding_judge_criteria);
        fd.append("coding_set", cfg.coding_set);
        fd.append("mcq_time_limit", cfg.mcq_time_limit);
        fd.append("coding_time_limit", cfg.coding_time_limit);
        fd.append("typing_time_limit", cfg.typing_time_limit);
        fd.append("start_date", startDate.toISOString());
        fd.append("end_date", endDate.toISOString());
        fd.append("job_id", cfg.job_id);
        fd.append("job_role", cfg.jobrole);
        fd.append("job_description", JSON.stringify(cfg.job_description));
        if (cfg.custom_questions?.length) fd.append("custom_questions", JSON.stringify(cfg.custom_questions));
        fd.append("interview_type", cfg.interview_type);
        fd.append("interview_timelimit", cfg.interview_timelimit);
        fd.append("orgid", orgId);
        fd.append("source_of_application", "Sourcing");
        fd.append("timezone", timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
        fd.append("timeslot", timeslot || "");
        await axios.post(`${host}/store_candidate_data`, fd, {
            headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "multipart/form-data" },
        });
    };

    // Move the candidates out of sourcing now that the ATS holds their interview. Returns
    // the ids that moved, or null if the marker couldn't be written — the caller must not
    // drop rows the server will hand straight back on the next fetch.
    const handOverToInterviews = async (candidateIds) => {
        if (!candidateIds.length) return [];
        try {
            await axios.post(`${SOURCING_URL}/sourcing/scheduled`, {
                candidate_ids: candidateIds,
                job_id: jobId,
            });
            return candidateIds;
        } catch {
            return null;
        }
    };

    const submitSchedule = async ({ startDate, endDate, timezone, timeslot }) => {
        const targets = scheduleTargets;
        const cfg = jobConfig;
        if (!targets?.length || !cfg) return;
        if (!(await confirmCompanyDetails("invite"))) return;
        setScheduleTargets(null);
        setScheduling(true);
        try {
            const results = await Promise.allSettled(
                targets.map(c => _postOne(c, cfg, { startDate, endDate, timezone, timeslot }))
            );
            // Only the ones the ATS actually accepted move — a failed schedule leaves that
            // candidate exactly where they were, still available to try again.
            const scheduledIds = targets
                .filter((_, i) => results[i].status === "fulfilled")
                .map(c => c.candidate_id)
                .filter(Boolean);
            const ok = results.filter(r => r.status === "fulfilled").length;
            const failed = results.length - ok;

            const moved = ok ? await handOverToInterviews(scheduledIds) : [];

            if (targets.length === 1) {
                if (ok) toast.success("Interview scheduled — moved to Interview Management.");
                else toast.error("Failed to schedule interview.");
            } else {
                (failed ? toast.warn : toast.success)(
                    `${ok} interview${ok !== 1 ? "s" : ""} scheduled${failed ? `, ${failed} failed` : ""}.`
                );
            }
            if (moved === null) {
                toast.warn("Scheduled, but this list couldn't be updated — refresh to see the change.");
            } else if (moved.length) {
                onScheduled?.(moved);
            }
        } finally {
            setScheduling(false);
        }
    };

    const scheduleUI = (
        <>
            {/* Schedule popup */}
            {schedulePopupOpen && (
                <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={() => setScheduleTargets(null)}>
                    <div className="bg-white dark:bg-[#1B1524] rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <ModernScheduleInterview
                            onClose={() => setScheduleTargets(null)}
                            onConfirm={(data) => submitSchedule({
                                startDate: data.startDate,
                                endDate: data.endDate,
                                timezone: data.timezone,
                                timeslot: data.timeslot,
                            })}
                        />
                    </div>
                </div>
            )}

            {/* Email-entry prompt (when the candidate has no email) */}
            {emailPromptFor && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[80] px-4" onClick={() => !emailSaving && setEmailPromptFor(null)}>
                    <div className="bg-white dark:bg-[#1B1524] rounded-2xl w-full max-w-md p-6 shadow-xl" onClick={e => e.stopPropagation()}>
                        <h2 className="text-base font-semibold text-gray-800 dark:text-white mb-1">Add an email to continue</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            <span className="font-medium text-gray-700 dark:text-gray-300">{emailPromptFor.candidate?.full_name || "This candidate"}</span> has no email on file.
                            Enter one to schedule the interview — we'll save it to the candidate.
                        </p>
                        <input
                            autoFocus
                            type="email"
                            autoComplete="off"
                            value={emailInput}
                            onChange={e => setEmailInput(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") saveEmailAndContinue(); }}
                            placeholder="name@company.com"
                            className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 outline-none focus:border-purple-400 mb-4"
                        />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setEmailPromptFor(null)} className="px-4 py-2 rounded-full text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-white/5">Cancel</button>
                            <button
                                onClick={saveEmailAndContinue}
                                disabled={emailSaving || !emailInput.trim()}
                                className="px-5 py-2 rounded-full text-sm font-medium text-white disabled:opacity-50"
                                style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                            >
                                {emailSaving ? "Saving…" : "Save & continue"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk email gate — add emails for any selected candidates that don't have one */}
            {bulkGate && (() => {
                const readyCount = bulkGate.filter(r => r.email).length;
                const missing = bulkGate.filter(r => !r.email);
                return (
                    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={() => setBulkGate(null)}>
                        <div className="bg-white dark:bg-[#1B1524] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
                            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Schedule {bulkGate.length} interview{bulkGate.length !== 1 ? "s" : ""}</h2>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                    {missing.length} of {bulkGate.length} {missing.length === 1 ? "candidate has" : "candidates have"} no email — add one, or continue with the {readyCount} ready.
                                </p>
                            </div>
                            <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-2">
                                {bulkGate.map(r => (
                                    <div key={r.candidate.candidate_id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-100 dark:border-gray-700/50">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{r.candidate.full_name || "Unknown"}</p>
                                            {r.email
                                                ? <p className="text-xs text-green-600 dark:text-green-400 truncate">{r.email}</p>
                                                : <p className="text-xs text-amber-500">No email on file</p>}
                                        </div>
                                        {!r.email && (
                                            r.adding ? (
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <input
                                                        type="email"
                                                        autoComplete="off"
                                                        autoFocus
                                                        value={r.value}
                                                        onChange={e => setBulkGate(prev => prev.map(x => x.candidate.candidate_id === r.candidate.candidate_id ? { ...x, value: e.target.value } : x))}
                                                        onKeyDown={e => { if (e.key === "Enter") gateAddEmail(r.candidate.candidate_id); }}
                                                        placeholder="name@company.com"
                                                        className="w-52 text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 outline-none focus:border-purple-400"
                                                    />
                                                    <button onClick={() => gateAddEmail(r.candidate.candidate_id)} disabled={!r.value.trim()} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white disabled:opacity-40" style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}>Save</button>
                                                </div>
                                            ) : (
                                                <button onClick={() => setBulkGate(prev => prev.map(x => x.candidate.candidate_id === r.candidate.candidate_id ? { ...x, adding: true } : x))} className="px-3 py-1.5 rounded-lg text-xs text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-white/5 shrink-0">Add email</button>
                                            )
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700/50 flex items-center justify-end gap-3 shrink-0">
                                <button onClick={() => setBulkGate(null)} className="px-5 py-2 rounded-full text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500">Cancel</button>
                                <button onClick={gateContinue} disabled={readyCount === 0} className="px-5 py-2 rounded-full text-sm font-medium text-white disabled:opacity-40" style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}>
                                    Continue ({readyCount})
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </>
    );

    return { startSchedule, startBulkSchedule, scheduleUI, scheduling, canSchedule: !!jobId };
}
